import { useMemo } from 'react';

/**
 * CSS/SVG starfield — pure GPU animation, no JS frame loop.
 *
 * Three layers of stars at different scales drift at different speeds to
 * give parallax. Each star also twinkles via opacity + scale.
 *
 * Pure SVG → renders inside any container, scales with the viewport,
 * costs ~0 KB on the wire.
 */
interface Star {
  cx: number;
  cy: number;
  r: number;
  delay: number;
}

function generateStars(count: number, seed: number): Star[] {
  // Deterministic pseudo-random so SSR/hydration matches if we ever SSR.
  let s = seed;
  const rand = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  return Array.from({ length: count }, () => ({
    cx: rand() * 100,
    cy: rand() * 100,
    r: 0.3 + rand() * 1.2,
    delay: rand() * 6,
  }));
}

export default function Starfield() {
  const small = useMemo(() => generateStars(80, 1), []);
  const medium = useMemo(() => generateStars(30, 2), []);
  const large = useMemo(() => generateStars(8, 3), []);

  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {/* Nebula gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 70% 20%, rgba(120,80,180,0.35) 0%, transparent 55%),' +
            'radial-gradient(ellipse at 20% 90%, rgba(80,40,140,0.30) 0%, transparent 60%),' +
            'linear-gradient(180deg, #0a0518 0%, #15102e 50%, #0b0820 100%)',
        }}
      />

      <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 100">
        {small.map((s, i) => (
          <circle
            key={`s${i}`}
            cx={s.cx}
            cy={s.cy}
            r={s.r * 0.18}
            fill="#e8c356"
            opacity="0.5"
          >
            <animate
              attributeName="opacity"
              values="0.2;0.8;0.2"
              dur="4s"
              begin={`${s.delay}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}
        {medium.map((s, i) => (
          <circle
            key={`m${i}`}
            cx={s.cx}
            cy={s.cy}
            r={s.r * 0.3}
            fill="#f5d97a"
            opacity="0.7"
          >
            <animate
              attributeName="opacity"
              values="0.4;1;0.4"
              dur="6s"
              begin={`${s.delay}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}
        {large.map((s, i) => (
          <g key={`l${i}`} transform={`translate(${s.cx} ${s.cy})`}>
            <circle r={s.r * 0.5} fill="#fff8e0" opacity="0.9">
              <animate
                attributeName="opacity"
                values="0.6;1;0.6"
                dur="3s"
                begin={`${s.delay}s`}
                repeatCount="indefinite"
              />
            </circle>
            {/* 4-point sparkle ray */}
            <path
              d={`M0 ${-s.r * 2.5} L0 ${s.r * 2.5} M${-s.r * 2.5} 0 L${s.r * 2.5} 0`}
              stroke="#fff8e0"
              strokeWidth="0.05"
              opacity="0.6"
            >
              <animate
                attributeName="opacity"
                values="0.3;0.8;0.3"
                dur="3s"
                begin={`${s.delay}s`}
                repeatCount="indefinite"
              />
            </path>
          </g>
        ))}
      </svg>
    </div>
  );
}
